/**
 * Fotos de documentación: qué es una foto y cómo se achica antes de subirla.
 *
 * POR QUÉ ESTO EXISTE
 * ===================
 * El comprador pidió poder subir *fotos* de documentación en cada ficha. Una
 * foto sacada con un celular de hoy pesa entre 3 y 6 MB, y eso trae dos
 * problemas que no son el mismo:
 *
 * 1. **El disco y la copia de seguridad.** Veinte fichas con cinco fotos cada
 *    una son medio giga de archivos, y desde que existen adjuntos el respaldo
 *    ya no es sólo `db.sqlite3`: hay que llevarse también la carpeta de
 *    archivos o al restaurar quedan todos en 404.
 * 2. **La galería.** Las miniaturas se arman del lado del cliente —bajando el
 *    archivo y haciéndole `createObjectURL`— porque el backend **nunca** sirve
 *    un adjunto `inline`; lo dice `core/attachments_api.py` con todas las
 *    letras y el motivo es que una imagen servida en el mismo origen que la
 *    aplicación es un XSS almacenado esperando el tipo de archivo equivocado.
 *    O sea: para dibujar la grilla hay que bajar los originales. Con fotos de
 *    4 MB, abrir una ficha con seis fotos son 24 MB.
 *
 * LA DECISIÓN: se achican en el navegador, ANTES de subirlas
 * ----------------------------------------------------------
 * Una foto de 4032×3024 se reescala a 2000 px de lado mayor y se reencoda como
 * JPEG de calidad 0,82. Queda entre 300 y 700 kB —de seis a diez veces menos— y
 * a 2000 px el texto de un DNI, de una escritura o de un formulario sigue
 * siendo legible, que es para lo único que estas fotos existen. El original no
 * se guarda: guardar los dos sería no haber resuelto nada.
 *
 * Es una pérdida de información y hay que decirlo de frente: quien suba una
 * foto de 12 MP no va a poder bajar después una foto de 12 MP. A cambio, la
 * función de subir fotos es usable con la conexión de una oficina y el respaldo
 * sigue cabiendo en un pendrive. Si algún día hace falta el original (un
 * peritaje, una prueba judicial), esto es lo que hay que revisar primero.
 *
 * LO QUE NO SE TOCA, y cada exclusión tiene su motivo
 * ---------------------------------------------------
 * * **Lo que no es imagen.** Un PDF pasa igual: reencodarlo no tiene sentido y
 *   además es el formato en el que llegan la mayoría de los papeles.
 * * **SVG.** Pasarlo por un canvas lo rasteriza: entra un vector y sale un
 *   mapa de bits borroso al ampliar. Y pesa kilobytes, así que no hay nada que
 *   ganar.
 * * **GIF.** Reencodar deja el primer cuadro y tira la animación.
 * * **Lo que ya es chico.** Debajo del umbral no se toca: reencodar una captura
 *   de pantalla de 200 kB la puede dejar MÁS pesada y encima con artefactos.
 * * **Lo que el navegador no sabe decodificar** (HEIC en la mayoría). Ahí
 *   `createImageBitmap` falla, y la respuesta correcta es subir el original tal
 *   cual: mejor una foto pesada que ninguna foto.
 *
 * Y una salvaguarda al final: si después de todo el trabajo el resultado pesa
 * MÁS que el original, se sube el original. Puede pasar con imágenes ya
 * optimizadas o con mucho ruido.
 *
 * SOBRE EL ARCHIVO SUELTO DEL COMPRADOR
 * -------------------------------------
 * El peso de las fotos **no** llega a `muestra/Nucleo-ERP-sistema.html`, y esto
 * está verificado, no supuesto: `tools/exportar_foto_demo.py` exporta sólo el
 * LISTADO de adjuntos de cada ficha (el JSON de `GET /attachments/…`), nunca los
 * bytes, y el servidor falso de `offline/servidor.ts` devuelve un archivo
 * generado al vuelo cuando alguien pide una descarga. O sea que una foto de 4 MB
 * no puede reventar el archivo suelto porque nunca entra. Lo que este módulo
 * protege es el sistema de verdad.
 */

/** Lado mayor al que se reduce una foto. Ver el docstring: 2000 px deja legible el texto de un documento. */
const LADO_MAXIMO = 2000;

/** Calidad del JPEG de salida. 0,82 es donde deja de notarse a simple vista. */
const CALIDAD = 0.82;

/**
 * Debajo de esto no se toca nada.
 *
 * 600 kB es aproximadamente lo que pesa una foto ya razonable. Reencodar algo
 * más chico no ahorra nada y sí puede empeorarlo.
 */
const UMBRAL_BYTES = 600 * 1024;

/**
 * Formatos de imagen que NO se reencodan, con su motivo en el docstring del
 * módulo. Se listan por exclusión y no por inclusión a propósito: un formato
 * nuevo que el navegador sepa decodificar es un formato que conviene achicar.
 */
const FORMATOS_INTOCABLES = new Set(['image/svg+xml', 'image/gif']);

/** Si el adjunto se tiene que dibujar como foto y no como una línea de lista. */
export function esFoto(mimetype: string): boolean {
  return mimetype.startsWith('image/');
}

/** Qué pasó con el archivo, para poder contárselo a quien lo subió. */
export interface FotoPreparada {
  /** Lo que hay que subir: la versión reducida, o el original si no se tocó. */
  archivo: File;
  /** Bytes del original, para el aviso. */
  bytesOriginales: number;
  /** `true` sólo si de verdad se reemplazó por una versión más liviana. */
  seRedujo: boolean;
}

/**
 * Si vale la pena intentar achicar este archivo.
 *
 * Está separada de la parte que usa el canvas justamente para poder probarla:
 * toda la decisión vive acá y es aritmética pura, mientras que el reescalado
 * necesita un navegador de verdad.
 */
export function hayQueAchicar(tipo: string, bytes: number): boolean {
  if (!esFoto(tipo)) return false;
  if (FORMATOS_INTOCABLES.has(tipo)) return false;
  return bytes > UMBRAL_BYTES;
}

/** Las medidas de salida para un original de `ancho`×`alto`. Nunca agranda. */
export function medidasReducidas(
  ancho: number,
  alto: number,
  ladoMaximo: number = LADO_MAXIMO,
): { ancho: number; alto: number } {
  const mayor = Math.max(ancho, alto);
  if (mayor <= ladoMaximo) return { ancho, alto };
  const escala = ladoMaximo / mayor;
  // `round` y no `floor`: con `floor`, una imagen de 1×N podía terminar en 0 y
  // el canvas de ancho cero tira una excepción.
  return { ancho: Math.max(1, Math.round(ancho * escala)), alto: Math.max(1, Math.round(alto * escala)) };
}

/** El nombre con extensión `.jpg`, porque el contenido ahora ES un JPEG. */
function nombreComoJpg(nombre: string): string {
  const punto = nombre.lastIndexOf('.');
  const base = punto > 0 ? nombre.slice(0, punto) : nombre;
  return `${base}.jpg`;
}

/**
 * Devuelve la foto lista para subir.
 *
 * **Nunca lanza.** Cualquier problema —un formato que el navegador no decodifica,
 * un canvas que no se puede crear, una API que no existe en este navegador—
 * termina devolviendo el archivo original sin tocar. Fallar en achicar una foto
 * no puede impedir subirla.
 */
export async function prepararFotoParaSubir(archivo: File): Promise<FotoPreparada> {
  const intacto: FotoPreparada = {
    archivo,
    bytesOriginales: archivo.size,
    seRedujo: false,
  };
  if (!hayQueAchicar(archivo.type, archivo.size)) return intacto;
  if (typeof createImageBitmap !== 'function') return intacto;

  try {
    // `imageOrientation: 'from-image'` NO es opcional: sin él, una foto sacada
    // en vertical con el celular se sube acostada, porque la rotación vive en
    // los metadatos EXIF y el canvas los ignora.
    const bitmap = await createImageBitmap(archivo, { imageOrientation: 'from-image' });
    const { ancho, alto } = medidasReducidas(bitmap.width, bitmap.height);

    const lienzo = document.createElement('canvas');
    lienzo.width = ancho;
    lienzo.height = alto;
    const pincel = lienzo.getContext('2d');
    if (pincel === null) {
      bitmap.close();
      return intacto;
    }
    pincel.drawImage(bitmap, 0, 0, ancho, alto);
    bitmap.close();

    const reducido = await new Promise<Blob | null>((resolver) => {
      lienzo.toBlob(resolver, 'image/jpeg', CALIDAD);
    });
    // Se libera el lienzo a mano: en iOS el canvas grande queda ocupando
    // memoria de video hasta que el recolector pasa, y subir seis fotos
    // seguidas es justo el caso en que eso se nota.
    lienzo.width = 0;
    lienzo.height = 0;

    if (reducido === null) return intacto;
    // La salvaguarda: si no ahorramos nada, se sube el original. Es mejor un
    // archivo grande que uno grande Y degradado.
    if (reducido.size >= archivo.size) return intacto;

    return {
      archivo: new File([reducido], nombreComoJpg(archivo.name), {
        type: 'image/jpeg',
        lastModified: archivo.lastModified,
      }),
      bytesOriginales: archivo.size,
      seRedujo: true,
    };
  } catch {
    // HEIC, un formato roto, un navegador sin soporte: el original va igual.
    return intacto;
  }
}
